<script>
import { defineAsyncComponent } from "@common/defineAsyncComponent";
import { componentStore } from "@common/componentStore/componentStore";
import { library } from "@common/library/library";
import { configStore } from "@common/configStore";

const locals = {
    formatWorkflow(workflowConfig, relatedRecords, userStore, editorVisible, _this) {
        // Variables and exit conditions
        if (typeof workflowConfig !== 'object') return [];
        if (workflowConfig.ACTIVITY_ENTITY) workflowConfig.activities = workflowConfig.ACTIVITY_ENTITY;
        let activities = typeof workflowConfig.activities === 'object'
            && Object.values(workflowConfig.activities);
        if (!Array.isArray(activities)) return [];

        let getStepRecords = (step) => {
            // Returns the correct records for each activity (step)
            let recordMatcher = (type) => {
                if (!relatedRecords || typeof relatedRecords[type] !== 'object') return false;
                let records = library.cloneObj(relatedRecords[type]);
                if (!Array.isArray(records)) records = Object.values(records);
                return records.find((record) => record && record.ACTIVITY_ENTITY_ID === step.ACTIVITY_ENTITY_ID);
            };
            return {
                aff: recordMatcher('affiliations'),
                lbe: recordMatcher('lbes'),
                waiver: recordMatcher('waivers'),
                training: recordMatcher('trainings'),
                shift: recordMatcher('shifts'),
            };
        };
        // Mapping/Formatting Functions
        let setLogicRecords = (serverLogic, step) => {
            let output = [];
            let records = step.relatedRecords;
            if (!library.isJson(serverLogic)) return output;
            let logicObj = JSON.parse(serverLogic);
            let logicRule = logicObj['[FilterEditor::FiltersColumns]'];
            if (Array.isArray(logicRule)) {
                for (let i in logicRule) {
                    if (i === 'length') continue;
                    if (typeof logicRule[i].CLAUSE_TITLE !== 'string') continue;
                    let logicField = logicRule[i].CLAUSE_TITLE.toLowerCase();
                    output[i] = { criteriaRecord: 'dynamically' };
                    // Set the perspective
                    let logicPsp = logicRule[i].FLT_SELECT_FIELD_PERSPECTIVE_ABBREV;
                    if (logicPsp === 'lbe') {
                        if (logicRule[i].LBE_TYPE === 5) logicPsp = 'waiver'; // NOSONAR
                        if (logicRule[i].LBE_TYPE === 6) logicPsp = 'training'; // NOSONAR
                    }
                    let assignmentSubRecord = logicRule[i].FLT_CLAUSE_ADDITIONAL_PARAM; // Sub-record is related orgs (eg: tier 500) ie: orgKind
                    // Check for is assignment record
                    if (logicField === 'aff:assignment status') {
                        output[i].recordId = relatedRecords.ASSIGNMENT_ID;
                    }
                    // Check for a selected "Sub Record" from the assignment
                    else if (assignmentSubRecord && assignmentSubRecord !== 2) {
                        if (assignmentSubRecord === 1) output[i].recordId = relatedRecords.ASSIGNMENT_ID;
                        else if (assignmentSubRecord === 9999) output[i].recordId = relatedRecords.DETAIL_RECORD_ID; // NOSONAR
                        else if (logicField.indexOf('aff') === 0) output[i].recordId = relatedRecords?.ASSIGNMENT?.LINKED_AFFILIATIONS?.[assignmentSubRecord];
                        else output[i].recordId = relatedRecords?.ASSIGNMENT?.LINKED_RECORDS?.[assignmentSubRecord];
                    }
                    // Otherwise link using the perspective
                    else if (logicPsp === 'aff') output[i].recordId = records.aff && records.aff.AFFILIATION_ID;
                    else if (logicPsp === 'lbe') output[i].recordId = records.lbe && records.lbe.LOG_BOOK_ENTRY_ID;
                    else if (logicPsp === 'waiver') output[i].recordId = records.waiver && records.waiver.LOG_BOOK_ENTRY_ID;
                    else if (logicPsp === 'training') output[i].recordId = records.training && records.training.LOG_BOOK_ENTRY_ID;
                    else if (logicPsp === 'sch') {
                        // output[i].recordId = records.shift && records.shift.SCHEDULE_SLOT_ID;
                        output[i].criteriaRecord = 'object2';
                    }
                    else if (logicPsp === 'client') {
                        output[i].criteriaRecord = 'component.ecUserProperty';
                    }
                }
            }
            return output;
        };
        let mapTemplates = (tItem, step, hideWhenComplete, hideWhenDisabled) => {
            tItem.template = tItem.TEXT;
            tItem.include = tItem.IS_DEFAULT !== 0 || !tItem.LOGIC;
            if (tItem.LOGIC) {
                let logicRecords = setLogicRecords(tItem.LOGIC, step);
                let criteria = library.parseLogicToFront(tItem.LOGIC, logicRecords);
                tItem.includeWhen = [{ criteria, resultType: 'returnCriteria' }];
            }
            tItem.hideWhenDisabled = hideWhenDisabled;
            tItem.hideWhenComplete = hideWhenComplete;
            return tItem;
        };
        let parseActionLogic = (action, step, logicType) => {
            if (action[logicType]) {
                let logicRecords = setLogicRecords(action[logicType], step);
                let criteria = library.parseLogicToFront(action[logicType], logicRecords);
                return [{ criteria, resultType: 'returnCriteria' }];
            }
            return false;
        };
        let mapActions = (action, step, hideWhenComplete, hideWhenDisabled) => {
            let tAction = library.cloneObj(action);
            tAction.label = tAction.TEXT;
            // Convert legacy config to standard
            // TODO: Remove this code after all configurations have been updated https://samaritan.atlassian.net/browse/DEVD-1202
            if (tAction.ACTION
                && typeof tAction.ACTION === 'object'
                && !Array.isArray(tAction.ACTION)
                && tAction.ACTION.params
            ) {
                let chainedActions = [];
                if (tAction.ACTION.params.actionChaining) {
                    chainedActions = library.cloneObj(tAction.ACTION.params.actionChaining);
                    delete tAction.ACTION.params.actionChaining;
                }
                let formattedVal = [tAction.ACTION];
                formattedVal = formattedVal.concat(chainedActions);
                tAction.action = formattedVal;
            } else tAction.action = tAction.ACTION;
            tAction.actionIcon = tAction.ACTION_ICON;
            tAction.include = !!tAction.IS_DEFAULT;
            if (tAction.CSS_CLASSES) tAction.cssClasses = tAction.CSS_CLASSES;

            tAction.includeWhen = parseActionLogic(tAction, step, 'LOGIC');
            tAction.disableWhen = parseActionLogic(tAction, step, 'DISABLE_LOGIC');

            tAction.hideWhenDisabled = hideWhenDisabled;
            tAction.hideWhenComplete = hideWhenComplete;
            return tAction;
        };
        let processStepLogic = (step) => {
            let v1 = 1; // By Default
            let v2 = 2; // Use Logic Editor
            let v3 = 3; // Use Volunteer's Affiliation Status
            // Set include property
            let setStepState = (stepKey) => {
                // stepKey is one of: include, enable, complete
                let partialKey = 'AE_' + stepKey.toUpperCase() + 'D'; // eg: AE_INCLUDED, AE_ENABLED, AE_COMPLETED
                let sOption = step[partialKey + '_BY_DEFAULT']; // eg: 1, 2, 3
                let affStatusKey = partialKey + '_AFF_STATUS_ID'; // eg: AE_INCLUDED_AFF_STATUS_ID
                let logicKey = partialKey + '_LOGIC_FILTER_ID'; // eg: AE_INCLUDED_LOGIC_FILTER_ID
                let criteria;
                if (!sOption || sOption === v1) {
                    criteria = {
                        rules: [{
                            field: partialKey + '_BY_DEFAULT',
                            operator: 'equal',
                            type: 'numeric',
                            value: v1,
                            criteriaRecord: 'object1' // previous step completed
                        }],
                        condition: 'and'
                    };
                }
                else if (sOption === v2 && step[logicKey]) {
                    let logicRecords = setLogicRecords(step[logicKey], step);
                    criteria = library.parseLogicToFront(step[logicKey], logicRecords);
                }
                else if (sOption === v3) {
                    let stepRecords = step.relatedRecords;
                    // Map selected status option from activity profile to logic criteria
                    if (stepRecords.aff) {
                        criteria = {
                            rules: [{
                                label: 'AFF:Status',
                                field: 'AFF_STATUS_ID',
                                operator: 'equal',
                                type: 'numeric',
                                value: step[affStatusKey],
                                psp: 'aff',
                                criteriaRecord: 'dynamically',
                                recordId: stepRecords.aff.AFFILIATION_ID
                            }],
                            condition: 'and'
                        };
                    }
                }
                if (criteria) step[stepKey + 'When'] = [{ criteria, resultType: 'returnCriteria' }];
            };
            setStepState('include');
            setStepState('enable');
            setStepState('complete');
            let setOnChangeActions = () => {
                if (library.optimization.disableItemListLogic) return;
                if (!step['AE_ENABLED_SET'] || workflowConfig?.WORKFLOW_DISABLE_AUTO_STATUS_UPDATES) return; // Only care if we have the setting
                const stepRecords = step.relatedRecords;
                const currStatus = stepRecords?.aff?.AFF_STATUS_ID;
                const newStatus = parseInt(step.AE_ENABLED_SET_AFF_STATUS_ID, 10) > 0 && step.AE_ENABLED_SET_AFF_STATUS_ID;
                const newReason = parseInt(step.AE_ENABLED_SET_AFF_REASON_ID, 10) > 0 && step.AE_ENABLED_SET_AFF_REASON_ID;
                const defaultStatus = parseInt(step.AE_DEFAULT_AFF_STATUS_ID, 10) > 0 && step.AE_DEFAULT_AFF_STATUS_ID;
                const newDate = typeof step.AE_ENABLED_SET_AFF_START_DATE === 'string' && step.AE_ENABLED_SET_AFF_START_DATE ? step.AE_ENABLED_SET_AFF_START_DATE : false;

                if (!newStatus || currStatus !== defaultStatus) return;

                let changeField;
                let criteriaObj;

                switch (+step.AE_ENABLED_SET) {
                    case 1: // Previous step is complete
                        changeField = 'complete';
                        criteriaObj = 'object3';
                        break;
                    case 2: // All previous steps are complete
                        changeField = 'previousStepsComplete';
                        criteriaObj = 'object1';
                        break;
                    // additional cases are coming soon. FT-531
                    default:
                        changeField = false;
                        criteriaObj = false;
                }
                // Map selected status option from activity profile to logic criteria
                const onChangeCriteria = {
                    rules: [{ // This rule is intentionally duplicating the logic in line above "stepRecords.aff.AFF_STATUS_ID..."
                        label: 'AFF:Status ID',
                        field: 'AFF_STATUS_ID',
                        operator: 'equal',
                        type: 'numeric',
                        value: defaultStatus, // TODO Change this to be the default value
                        psp: 'aff',
                        criteriaRecord: 'dynamically',
                        recordId: stepRecords.aff.AFFILIATION_ID
                    }, {
                        field: changeField,
                        operator: 'equal',
                        type: 'boolean',
                        value: true,
                        criteriaRecord: criteriaObj // previous step completed
                    }],
                    condition: 'and'
                };
                // Put the complete onChangeActions configuration into the step configuration
                const onChangeActions = [
                    {
                        logic: [{
                            criteria: onChangeCriteria,
                            resultType: 'returnCriteria',
                        }],
                        actions: [
                            {
                                action: 'updateAffiliation',
                                params: {
                                    affID: stepRecords.aff.AFFILIATION_ID,
                                    AFF_STATUS: newStatus,
                                    AFF_REASON: newReason, // It might be false
                                    AFF_START_DATE: newDate,
                                    disableResponseMessage: true
                                }
                            }
                        ],
                        executeIfLogicResult: true,
                        runOnce: true
                    }
                ];
                step.onChangeActions = onChangeActions;
            };
            setOnChangeActions();
        };
        let processExpiration = (step) => {
            // return if status ID isn't 3 (expiring)
            let complianceStatus = step?.relatedRecords?.aff?.AFF_COMPLIANCE_STATUS_ID;
            let affStatus = step?.relatedRecords?.aff?.AFF_STATUS_ID;
            if (complianceStatus === 3 || (complianceStatus === 5 && affStatus !== 4)) step.cssClasses += ' item-expiring';
            if (complianceStatus !== 3) return;
            const EXPIRATION_DATE = step?.relatedRecords?.aff?.AFF_EXPIRATION_DATE || false;
            const EXPIRATION_MESSAGE = 'This task is about to expire, click the button to begin renewal.';
            const expirationTemplate = {
                template: `<div class="activity-expiration expiration-flag">
                                <i class="fa-solid fa-triangle-exclamation"></i>
                                <div class="expiration-text">
                                    <strong style="display: flex">
                                        Expiring Soon
                                        <span v-if="${EXPIRATION_DATE}"
                                              style="font-weight: normal; color: #4c4c4c; margin-left: 6px"
                                        >- ${EXPIRATION_DATE}</span>
                                    </strong>
                                    <span>
                                        ${EXPIRATION_MESSAGE}
                                    </span>
                                </div>
                                <button type="button"
                                        class="coordinator-action-button"
                                        title="Renew"
                                        @click="actionStore.renewActivity(
                                                    {
                                                        currentAffId: record.AFFILIATION_ID,
                                                        volId: record.VOLUNTEER_ID,
                                                        activityId: record.ACTIVITY_ENTITY_ID,
                                                        repeatOrgId: '',
                                                        fields: [
                                                            { field: 'AFF_STATUS', value: 3},
                                                            { field: 'AFF_REASON', value: 503},
                                                            { field: 'AFF_COMPLIANCE_STATUS', value: 5},
                                                        ]
                                                    })"
                                ><span>Renew</span>
                                </button>
                           </div>`,
                cssClasses: 'renew-message'
            };

            step.descriptions.unshift(expirationTemplate);
        };
        // EXECUTE: Process all the activities
        activities = library.cloneObj(activities);
        return activities.map((step) => {
            if (typeof step !== 'object') return {};
            step.relatedRecords = getStepRecords(step);
            step.aeId = step.ACTIVITY_ENTITY_ID;
            step.volId = _this.contextVolId || step.VOLUNTEER_ID;
            step.WORKFLOW_TITLE = workflowConfig['WORKFLOW_TITLE'];
            if (!library.objIsEmpty(step.relatedRecords)) {
                for (let psp in step.relatedRecords) {
                    if (typeof step.relatedRecords[psp]?.ID !== 'undefined') step[psp + 'Id'] = step.relatedRecords[psp].ID;
                }
            }
            if (typeof step.AE_CSSCLASSES !== 'undefined') step.cssClasses = step.AE_CSSCLASSES;
            if (step.AE_IS_COMPLIANCE) {
                const complianceClass = 'compliance-status-' + step?.relatedRecords?.aff?.AFF_COMPLIANCE_STATUS_ID;
                step.cssClasses = step.cssClasses === 'undefined' ? complianceClass : step.cssClasses + ' ' + complianceClass;
            }
            // Hide for coordinator users that cannot see the activity
            let isVisible = !userStore?.coordinator?.availableActivities
                || !!userStore.coordinator.availableActivities.find((ca) => step.ACTIVITY_ENTITY_ID === ca.ACTIVITY_ENTITY_ID);
            step.isVisible = isVisible;
            if (step.isVisible) {
                if (Array.isArray(step.ACTIVITY_ENTITY_TITLE)) {
                    if (!step.ACTIVITY_ENTITY_TITLE.length) step.titles = [{ TEXT: step.AE_TITLE, template: `<div>${step.AE_TITLE}</div>` }];
                    else step.titles = step.ACTIVITY_ENTITY_TITLE.map((title) => mapTemplates(title, step));
                }
                if (Array.isArray(step.ACTIVITY_ENTITY_MESSAGE)) step.descriptions = step.ACTIVITY_ENTITY_MESSAGE.map((description) => mapTemplates(description, step, !description.SHOW_WHEN_COMPLETE, true));
                if (Array.isArray(step.ACTIVITY_ENTITY_ACTION)) step.actions = step.ACTIVITY_ENTITY_ACTION.map((action) => mapActions(action, step, !action.SHOW_WHEN_COMPLETE, true));
            }
            else {
                step.titles = [{
                    template: "<div>Step Unavailable</div>",
                    include: true
                }];
                step.descriptions = [{
                    template: "<div>This step is not visible to you.</div>",
                    include: true
                }];
                step.actions = [];
                step.cssClasses += ' step-not-visible';
                step.excludeFromCount = true;
            }
            processStepLogic(step);
            // Set up the sub-record merging
            step.enableSubRecordMerging = true;
            step.subRecordMergeRules = [
                {
                    subSourceKey: 'affiliations',
                    subRecordFilter: 'this.subRecord.ACTIVITY_ENTITY_ID === this.item.ACTIVITY_ENTITY_ID',
                    mergeWithParent: true,
                    // If changing this, also change the history template that is added to ste descriptions
                },
                {
                    subSourceKey: 'waivers',
                    subRecordFilter: 'this.subRecord.LOGBOOK_AFFILIATION_ID === this.record.AFFILIATION_ID',
                    parentOutputKey: 'stepWaiver',
                },
                {
                    subSourceKey: 'shifts',
                    subRecordFilter: 'this.subRecord.AE_AFFILIATION_ID === this.record.AFFILIATION_ID',
                    mergeWithParent: true,
                },
                {
                    subSourceKey: 'lbes',
                    subRecordFilter: 'this.subRecord.LOGBOOK_AFFILIATION_ID === this.record.AFFILIATION_ID',
                    parentOutputKey: 'stepLbe',
                },
                {
                    subSourceKey: 'trainings',
                    subRecordFilter: 'this.subRecord.LOGBOOK_AFFILIATION_ID === this.record.AFFILIATION_ID',
                    parentOutputKey: 'stepTraining',
                }
            ];
            if (!editorVisible) {
                let adminControls = {
                    template: `<div>
                        <button type="button" class="coordinator-action-button" title="Edit Affiliation"
                            @click="actionStore.editAffiliation(
                                {
                                    affID: record.AFFILIATION_ID,
                                    dialogTitle: 'Edit Affiliation: ' + record.AE_TITLE + ' for ' + record.AFF_VOLUNTEER,
                                    showInDialog: {
                                        AFF_STATUS: true,
                                        AFF_REASON: true,
                                        AFF_NOTE: true,
                                        AFF_START_DATE_TYPE: true,
                                        AFF_START_DATE: true,
                                        AFF_EXPIRATION_DATE_TYPE: true,
                                        AFF_EXPIRATION_DATE: true
                                    }
                                })"
                        ><span class="h-button-text">Edit</span></button>
                    </div>`,
                    cssClasses: 'coordinator-actions',
                    includeWhen: [{
                        criteria: {
                            rules: [{
                                field: 'CLIENT_IS_ADMIN',
                                type: 'string',
                                operator: 'equal',
                                value: 'yes',
                                criteriaRecord: 'component.ecUserProperty'
                            }],
                            condition: 'and'
                        },
                        resultType: 'returnCriteria'
                    }]
                };
                let coordinatorControls = {
                    template: `<div>
                        <button type="button" class="coordinator-action-button" title="View History"
                            @click="actionStore.openComponentDialog(
                                {
                                    componentId: 'itemList_affiliation_history__workflow_step_' + index,
                                    contextRecordIds: { affId: record.AFFILIATION_ID },
                                    dialogSettings: { title: 'Activity History: ' + record.AE_TITLE + ' for ' + record.AFF_VOLUNTEER },
                                    component: {
                                        volId: component.volId,
                                        oppId: component.oppId,
                                        orgId: component.orgId,
                                        lbeId: component.lbeId,
                                        schId: component.schId,
                                        affId: component.affId,
                                        aeId: component.aeId
                                    }
                                }
                            )"
                        ><span class="h-button-text">View History</span></button>
                    </div>`,
                    cssClasses: 'coordinator-actions',
                    includeWhen: [{
                        criteria: {
                            rules: [{
                                field: 'CLIENT_ROLE_OPTION',
                                type: 'string',
                                operator: 'isnotempty',
                                criteriaRecord: 'component.ecUserProperty'
                            }],
                            condition: 'and'
                        },
                        resultType: 'returnCriteria'
                    }]
                };
                let coordinatorMessages = {
                    template: `<div>
                        <div class="aff-info-text">{{record.affStatusText}}</div>
                        <div class="aff-info-text">{{record.affReasonText}}</div>
                        <div class="aff-info-text">(ID: {{record.AFFILIATION_ID}})</div>
                    </div>`,
                    cssClasses: 'coordinator-info',
                    includeWhen: [{
                        criteria: {
                            rules: [{
                                field: 'CLIENT_ROLE_OPTION',
                                type: 'string',
                                operator: 'isnotempty',
                                criteriaRecord: 'component.ecUserProperty'
                            }],
                            condition: 'and'
                        },
                        resultType: 'returnCriteria'
                    }]
                };
                let refreshStep = {
                    template: `<div>
                        <button type="button" class="coordinator-action-button btn-renewal" title="Refresh Step"
                            @click="actionStore.refreshWorkflowStep({ record })"
                        ><span class="h-button-text">Refresh</span></button>
                    </div>`,
                    cssClasses: 'coordinator-actions',
                    includeWhen: [{
                        criteria: {
                            rules: [{
                                field: 'CLIENT_ROLE_OPTION',
                                type: 'string',
                                operator: 'isnotempty',
                                criteriaRecord: 'component.ecUserProperty'
                            }],
                            condition: 'and'
                        },
                        resultType: 'returnCriteria'
                    }]
                };
                let expirationMessage = {
                    template: `<div v-if="record.AFF_EXPIRATION_DATE">
                        <span class="expiration-flag"
                            v-if="moment().isSameOrAfter(moment(record.AFF_EXPIRATION_DATE, 'MM/DD/YYYY').subtract(30, 'days'))
                            && moment().isBefore(moment(record.AFF_EXPIRATION_DATE, 'MM/DD/YYYY'))">
                            Status: <span class="expiring">Expiring soon {{record.AFF_EXPIRATION_DATE}}
                            <span v-if="record.AFF_COMPLIANCE_STATUS_ID === 5"> (Renewal Started)</span>
                            </span>
                        </span>
                        <span class="expiration-flag"
                              v-else-if="moment().isSameOrAfter(moment(record.AFF_EXPIRATION_DATE, 'MM/DD/YYYY'))"
                              >Status: <span class="expired">Expired {{record.AFF_EXPIRATION_DATE}}</span></span>
                        <span class="expiration-flag" v-else>
                            <span class="current">Current expires: {{record.AFF_EXPIRATION_DATE}}</span></span>
                    </div>`,
                    cssClasses: 'expiration-status'
                };
                let renewalInProgress = {
                    template: `<div v-if="record.renewalExpDate && record.AFF_STATUS_ID === 3">
                                    <p>Renewal {{record.affStatusText}} - Expires: {{record.renewalExpDate}}</p>
                               </div>`,
                    cssClasses: 'renewal-in-progress-msg'
                };
                processExpiration(step);
                step.descriptions.push(...[expirationMessage, renewalInProgress, coordinatorMessages, coordinatorControls, adminControls, refreshStep]);
            }
            // TODO: Add other merge rules
            return step;
        });
    },
    formatAssignmentIds(records, affiliations, entryPointIds) {
        let output = {
            LINKED_RECORDS: {},
            LINKED_AFFILIATIONS: {},
            ENTRY_POINT_RECORDS: {
                hierarchy1: { ID: entryPointIds[0] },
                hierarchy2: { ID: entryPointIds[1] },
            }
        };
        for (let key in records) {
            if (key === 'length') continue;
            if (key === 'ORGANIZATIONS') {
                output[key] = records[key];
                continue;
            }
            output.LINKED_RECORDS[key] = { ID: records[key] };
            output.LINKED_AFFILIATIONS[key] = { ID: affiliations[key] };
        }
        return output;
    },
    formatWorkflowActivityEntities(workflowDetails, activityEntitiesDetails) {
        workflowDetails.ACTIVITY_ENTITY = [];
        for (let aeId in workflowDetails.ACTIVITY_ENTITY_ID) {
            if (activityEntitiesDetails[workflowDetails.ACTIVITY_ENTITY_ID[aeId]]) {
                workflowDetails.ACTIVITY_ENTITY.push(activityEntitiesDetails[workflowDetails.ACTIVITY_ENTITY_ID[aeId]]);
            }
        }
        return workflowDetails;
    }
};

const editorSettings = {
    generalSettingsPanel: {
        type: 'panel',
        label: 'General Settings',
        id: 'generalSettingsPanel',
        fields: [
            'restrictWorkflowTypes'
        ]
    },
    restrictWorkflowTypes: {
        type: 'CHECKBOX_LIST',
        label: 'Restrict Workflow Types',
        id: 'restrictWorkflowTypes',
        default: {},
        itemsSource: 'configStore.workflowOptions()',
        helpText: 'Pick a workflow type to filter. To show all, leave blank.',
        placeholder: 'Pick a workflow type to filter. To show all, leave blank.'
    },
};

const workflowViewerConfig = {
    cId: "workflowViewer",
    cType: "workflowViewer",
    cTitle: "Workflow Viewer",
    cTemplate: `<div v-if="logicParserReady">
            <div v-if="editorWorkflowConfig">
                <!-- Display editor workflow -->
                <item-list
                    @update:targetedItem="targetItem"
                    @update:itemOrder="updateOrder"
                    :items-list="editorWorkflowConfig"
                    :relative-records="editorRecord"
                    :prop-editor-visible="editorVisible"
                    :context-vol-id="volId"
                    c-id="itemList_workflow_editor__0"
                    c-type="itemList"
                ></item-list>
            </div>
            <div v-else-if="assignmentList && hasAssignments && workflowExists && (!assignmentsLoaded || !workflowTabs.length)" class="workflow-loader" style="padding: 15px;">
                <div class="loader-animation-container">
                    <div class="loader-animation">
                        <div class="ball"></div>
                        <div class="ball"></div>
                        <div class="ball"></div>
                        <div class="ball"></div>
                        <div class="ball"></div>
                    </div>
                </div>
                <p v-if="!assignmentsLoaded">Loading assignments... please wait.</p>
                <p v-if="!workflowTabs.length">Loading workflow tabs... please wait.</p>
            </div>
            <div v-else-if="assignmentsLoaded && !workflowExists" class="no-workflow">
                <p class="no-workflows-message">There are no workflows available.</p>
            </div>
            <div v-else-if="!hasAssignments" class="no-workflow">
                <p class="no-workflows-message">There are no assignments.</p>
            </div>
            <div v-if="workflowTabs.length">
                <div v-if="!visibleTabs && (childPendingSubscriptionsLength || !isWorkflowFullyLoadTimedout)" class="workflow-loader" style="padding: 15px;">
                    <div class="loader-animation-container">
                        <div class="loader-animation">
                            <div class="ball"></div>
                            <div class="ball"></div>
                            <div class="ball"></div>
                            <div class="ball"></div>
                            <div class="ball"></div>
                        </div>
                    </div>
                    <p v-if="waitingFirstWorkflowLoad">Loading workflows... please wait.</p>
                    <p v-if="!isWorkflowFullyLoadTimedout">Getting things ready... Just a few more seconds.</p>
                    <p v-else-if="!childPendingSubscriptionsLength">There are no visible workflows, some workflows are hidden.</p>
                </div>
                <div v-if="isWorkflowFullyLoadTimedout && !childPendingSubscriptionsLength" class="hidden-workflows-toggle">
                    <control name="SHOW_HIDDEN_WORKFLOWS"></control>
                </div>
                <div v-show="!waitingFirstWorkflowLoad">
                    <tab-list :cId="'tabList_workflow__' + cId" :prop-tabs-list="workflowTabs" :prop-selected-tab="selectedTab" @update:selectedTab="changeSelectedTab">
                        <template v-for="(tab, index) in workflowTabs" #[tab.slotTemplateId]>
                            <div class="workflow-header">
                                <div class="workflow-loader">
                                    <div class="loader-animation-container" v-show="!workflowTabsError?.[tab.wfTabId]" v-if="loaderVisible">
                                        <div class="loader-animation">
                                            <div class="ball"></div>
                                            <div class="ball"></div>
                                            <div class="ball"></div>
                                            <div class="ball"></div>
                                            <div class="ball"></div>
                                        </div>
                                    </div>
                                    <p v-show="!workflowTabsError?.[tab.wfTabId] && (!formattedWorkflowConfigs[tab.wfTabId] || !formattedWorkflowConfigs[tab.wfTabId].length)">
                                        Loading workflow details... Please wait.
                                    </p>
                                    <p v-show="!workflowTabsError?.[tab.wfTabId] && childPendingSubscriptionsLength">
                                        Getting step details... Almost there.
                                    </p>
                                </div>
                                <div class="workflow-status">
                                    <span class="assignment-id">(Assignment: {{tab.assignmentId}})</span>
                                    Workflow Status: {{tab.workflowStatus}}
                                    <button v-if="isAdminRole" type="button" class="coordinator-action-button" title="Update Status"
                                        @click="actionStore.updateWorkflowStatus({
                                        assignmentId: tab.assignmentId,
                                        workflowId: tab.workflowId})"><span class="h-button-text">Update Status</span>
                                    </button>
                                </div>
                            </div>
                            <div v-show="workflowTabsError?.[tab.wfTabId]">
                                <p>{{workflowTabsError[tab.wfTabId]}}</p>
                            </div>
                            <div v-if="formattedWorkflowConfigs[tab.wfTabId]" v-show="!workflowTabsError?.[tab.wfTabId]">
                                <div v-show="selectedTab === tab.tabIndex && tab.show">
                                    <item-list
                                        @update:includedItemsLength="updateIncludedItems(tab.wfTabId, $event)"
                                        @update:enabledItemsLength="updateEnabledItems(tab.wfTabId, $event)"
                                        :items-list="formattedWorkflowConfigs[tab.wfTabId]"
                                        :relative-records="workflowRecords[tab.wfTabId]"
                                        :prop-editor-visible="editorVisible"
                                        :context-vol-id="volId"
                                        :c-id="'itemList_workflow__' + cId + '_' + index"
                                        c-type="itemList"
                                        :ref="cId + '_itemList'"
                                        @update:pendingSubscriptions="updatePendingSubscriptions($event)"
                                    ></item-list>
                                </div>
                            </div>
                        </template>
                    </tab-list>
                </div>
            </div>
    </div>`,
    controls: [
        {
            name: 'SHOW_HIDDEN_WORKFLOWS',
            label: 'Show Hidden Workflows',
            type: 'boolean',
            inlineLabelReverse: true,
            logic: [{
                criteria: '!!this.component.ecUserProperty.CLIENT_ROLE_OPTION',
                prop: 'display',
                valueRecord: 'self',
                valueType: 'criteria'
            }],
            onchange: 'loadWorkflowTabs'
        }
    ]
};

const workflowViewerCmp = {
    name: "workflowViewer",
    editorSettings: editorSettings,
    props: {
        editorWorkflowSettings: {
            type: Object
        },
        workflowId: {
            default: false
        },
        editorVisible: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            targetedItem: false,
            workflowTabsSettings: {},
            workflowTabRequests: {}, // for request/response tracking only, not details
            workflowTabsError: {},
            workflowTabs: [],
            waitingFirstWorkflowLoad: true,
            serverAffiliations: {},
            statusOptions: [],
            reasonOptions: [],
            editorRecord: [{}], // Placeholder record
            relatedRecords: {
                // Configured from the sds_getWorkflowDetails response
                // Example:
                // 10: {
                //     affiliations: {},
                //     lbes: {},
                //     waivers: {},
                //     trainings: {},
                //     shifts: {},
                // }
            },
            shiftFields: [
                'SS_TITLE',
                'SS_DESC',
                'SS_NOTE',
                'SS_DATE',
                'SS_START',
                'SS_FINISH_TIME',
                'SS_END_TIME',
                'SS_START_TIME',
                'SS_PICKUP_INSTR',
                'SA_SSLI_ADDRESS_1',
                'SA_SSLI_ADDRESS_2',
                'SA_SSLI_CITY',
                'SA_SSLI_COUNTRY',
                'SA_SSLI_POSTAL_CODE',
                'SA_SSLI_STATE',
                'SA_SSLI_PHONE',
                'SA_SSLI_CONTACT_PERSON',
                'SA_SSLI_CONTACT_PHONE',
                'ACTIVITY_ENTITY_ID',
                'ASSIGNMENT_ID',
                'AE_AFFILIATION_ID'
            ],
            affFields: [
                'AFFILIATION_ID',
                'DETAIL_RECORD_ID',
                'AFF_REASON_ID',
                'AFF_REASON',
                'AFF_STATUS_ID',
                'AFF_STATUS',
                'AFF_START_DATE',
                'AFF_END_DATE',
                'AFF_COMPLIANCE_STATUS',
                'AFF_COMPLIANCE_STATUS_ID',
                'AFF_COMPLIANCE_STATUS_VERIFIER',
                'AFF_STATUS_VERIFIER_DATE',
                'ACTIVITY_ENTITY_TITLE',
                'ACTIVITY_ENTITY_ID',
                'AFF_ASSIGNMENT_WORKFLOW_STATUS',
                'AFF_STATUS_VERIFIER',
                'AFF_STATUS_DATETIME',
                'AFF_COMPLETE_DATE',
                'AFF_EXPIRATION_DATE',
                'AFF_VOLUNTEER',
                'AFF_EXPIRING_AFFILIATION_ID'
            ],
            user: false,
            hasAssignments: true,
            logicParserReady: false,
            selectedTab: null,
            workflowStepStatus: {},
            isWorkflowFullyLoad: false,
            isWorkflowFullyLoadTimedout: false,
            assignmentsLoaded: false,
            workflowExists: true,
            childPendingSubscriptions: [],
            childPendingSubscriptionsLength: 0,
            requestSettings: {
                startDate: library.getMoment().subtract(1, 'day').format('MM/DD/YYYY'),
                endDate: library.getMoment().add(60, 'days').format('MM/DD/YYYY'),
            }
        };
    },
    watch: {
        workflowStepStatus: {
            handler() {
                this.updateTabVisibility();
            },
            deep: true
        },
        contextOrgId(orgId) {
            const orgTab = this.workflowTabs.find((tab) => tab.orgId === orgId) || this.workflowTabs[0];
            // this.workflowTabs.forEach((tab) => { tab.show = tab.orgId === orgId || !orgId; });
            this.updateTabVisibility();
            this.changeSelectedTab(orgTab?.tabIndex);
        }
    },
    computed: {
        editorWorkflowConfig() {
            if (this.editorWorkflowSettings) return locals.formatWorkflow(this.editorWorkflowSettings, false, this.userStore, this.editorVisible, this);
            return false;
        },
        isAdminRole() {
            const coordinator = this.userStore.coordinator;
            return coordinator?.CLIENT_IS_ADMIN === 'Yes' || coordinator?.CLIENT_IS_STAFF === 'Yes';
        },
        formattedWorkflowConfigs() {
            // Take responses from sds_getWorkflowDetails from inside workflowTabs and format the workflow
            // This computed property powers the itemLists in the template
            let output = {};
            let workflowRecords = library.cloneObj(this.workflowRecords);
            if (this.editorWorkflowConfig || library.objIsEmpty(this.workflowTabsSettings)) return output;
            // Iterate through all workflows and format the config
            for (let tabId in this.workflowTabsSettings) {
                if (tabId === 'length') continue;
                let workflowConfig = this.workflowTabsSettings[tabId];
                let relatedRecords = workflowRecords[tabId] ? (workflowRecords[tabId]?.[0] || workflowRecords[tabId]) : false;
                if (typeof relatedRecords !== 'object' || !relatedRecords.recordsLoaded) continue;
                // TODO break formatWorkflow into smaller functions
                output[tabId] = locals.formatWorkflow(workflowConfig, relatedRecords, this.userStore, false, this);
                // Process include, enable and complete logic here so the itemList doesn't need to be loaded
                // TODO: Setup processing steps without having to load the itemList
            }
            return output;
        },
        workflowRecords() {
            let records = {};
            // Get various record subscriptions setup, wait for them to be complete before continuing to process
            // Get vol's activity shifts, but we aren't waiting for this to start rendering
            this.recordStore.subscribe({
                source: ['er_getPlacedSlotList'],
                updateFrequency: 60,
                params: {
                    volId: this.contextVolId,
                    fields: this.shiftFields,
                    activityEntitySlots: true,
                    startDate: this.requestSettings.startDate,
                    endDate: this.requestSettings.endDate
                }
            });
            let volSub = this.recordStore.subscribe({
                source: ['sds_getProfileField'],
                updateFrequency: 15,
                params: {
                    recordPsp: 'vol',
                    recordId: this.contextVolId,
                    fields: ['VOL_FIRST_NAME', 'VOL_LAST_NAME', 'VOL_AGE', 'VOLUNTEER_ID']
                }
            });
            let volRecord = library.cloneObj(this.recordStore.getSubscription(volSub));
            if (volRecord) volRecord = volRecord[this.contextVolId];
            let activityAffiliations = library.cloneObj(this.activityAffiliations);
            this.workflowTabs.forEach((tab) => {
                let wfId = tab.workflowId;
                let wfTabId = tab.wfTabId;
                if (!this.relatedRecords[wfTabId]) this.relatedRecords[wfTabId] = {};
                let relatedRecords = library.cloneObj(this.relatedRecords[wfTabId]);
                relatedRecords.affiliations = activityAffiliations[wfTabId];
                let record = {
                    WORKFLOW_ID: wfId,
                    shifts: volRecord?.shifts, // Use vol's shifts from the recordStore
                    assignmentId: relatedRecords?.ASSIGNMENT?.ID,
                    ASSIGNMENT_ID: relatedRecords?.ASSIGNMENT?.ID,
                    ...volRecord,
                    ...relatedRecords
                };
                record.recordsLoaded = !library.objIsEmpty(relatedRecords.affiliations) && !library.objIsEmpty(volRecord);
                if (record.recordsLoaded) records[wfTabId] = [record];
                else records[wfTabId] = [];
            });
            return library.cloneObj(records);
        },
        assignmentList() {
            if (this.contextVolId <= 0) return {};
            let assignmentsSub = this.recordStore.subscribe({
                source: ['sds_getAssignmentList'],
                updateFrequency: 360,
                params: {
                    volId: this.contextVolId,
                    fields: { 3: ['ORG_TITLE', 'ORGANIZATION_ID', 'WORKFLOW_ID', 'WORKFLOW_TITLE'], 32: ['AFF_ASSIGNMENT_WORKFLOW_STATUS', 'AFF_STATUS_ID', 'ENTRY_POINT_ORG_ID'] },
                    params: { strictTypeResults: true }
                },
                sDsParams: {
                    errors: {
                        show: true,
                        toastTimeout: 3,
                        toastMessage: 'Failed to load assignments, reloading...',
                        report: true,
                        forceReloadCount: 1
                    }
                }
            }, this.cId);
            if (assignmentsSub.lastRequestTime) {
                this.assignmentsLoaded = true; // Only sets to true on first loaded, not on polling updates
            }
            let records = library.cloneObj(this.recordStore.getSubscription(assignmentsSub.key));
            if (!library.objIsEmpty(records)) {
                setTimeout(() => {
                    this.hasAssignments = true;
                    this.loadWorkflowTabs();
                }, 10);
            }
            else if (this.assignmentsLoaded) { // Assignments subscription is returned and empty
                this.hasAssignments = false;
            }
            return records;
        },
        activityAffiliations() {
            let output = {};
            let subscriptionRecords = {};
            // Affiliation Functions
            let formatActivityAffiliations = (affiliations) => {
                if (typeof affiliations !== 'object') return {};
                for (let affId in affiliations) {
                    if (affId === 'length') continue;
                    let affRecord = affiliations[affId]; // This will be undefined until the subscription returns data
                    if (!affRecord) continue;
                    if (!affRecord.ACTIVITY_ENTITY_ID && affRecord.AFF_RECORDS?.[8]) {
                        affRecord.ACTIVITY_ENTITY_ID = Number(affRecord.AFF_RECORDS[8][0]);
                    }
                    if (!affRecord.HISTORY) affRecord.HISTORY = {};
                    affRecord.HISTORY = Object.values(affRecord.HISTORY).reverse();
                    // eslint-disable-next-line no-loop-func
                    let statusOption = this.statusOptions.find((status) => status.value === affRecord.AFF_STATUS_ID);
                    if (typeof statusOption?.text !== 'undefined') affRecord.affStatusText = statusOption.text;
                    // eslint-disable-next-line no-loop-func
                    let reasonOption = this.reasonOptions.find((status) => status.value === affRecord.AFF_REASON_ID);
                    if (typeof reasonOption?.text !== 'undefined') affRecord.affReasonText = reasonOption.text;
                    if (affRecord.AFF_EXPIRING_AFFILIATION_ID) affRecord.renewalExpDate = this.recordStore.records.aff?.[+affRecord.AFF_EXPIRING_AFFILIATION_ID]?.AFF_EXPIRATION_DATE;
                }
                // TODO setup a subscribe
                return affiliations;
            };
            const findRenewalAff = (affId) => {
                // Recurse until we have no new complianceRenewalAffId property on the record
                let newAffId = subscriptionRecords[affId]?.complianceRenewalAffId || affId;
                if (parseInt(newAffId, 10) !== parseInt(affId, 10) && subscriptionRecords[newAffId]?.complianceRenewalAffId) {
                    return findRenewalAff(subscriptionRecords[newAffId].complianceRenewalAffId);
                }
                // Add compliance expiration date to renewal record
                if (subscriptionRecords[affId]?.complianceRenewalAffId) {
                    this.recordStore.records.aff[newAffId].renewalExpDate = subscriptionRecords[affId]?.AFF_EXPIRATION_DATE;
                }
                return newAffId;
            };
            // if (!this.serverAffiliations[tab.wfTabId]) return {};
            // Subscribe to the vol->activity affiliations
            let allAffiliations = [];
            for (let tabIndex in this.workflowTabs) {
                let wfTabId = this.workflowTabs[tabIndex].wfTabId;
                if (!this.serverAffiliations[wfTabId]) continue;
                let affIds = Object.keys(this.serverAffiliations[wfTabId]);
                affIds = affIds.map((affId) => Number(affId));
                allAffiliations = allAffiliations.concat(affIds);
            }
            allAffiliations = [...new Set(allAffiliations)];
            if (!allAffiliations.length) return output;
            let payload = {
                source: 'sds_getProfileField',
                params: {
                    recordPsp: 'aff',
                    recordId: allAffiliations,
                    fields: this.affFields,
                    // params: { strictTypeResults: true }
                },
                updateFrequency: 30,
            };
            let subscription = this.recordStore.subscribe(payload, this.cId, false, false);
            subscriptionRecords = library.cloneObj(this.recordStore.getSubscription(subscription.key));
            if (subscriptionRecords) {
                // Iterate and substitute
                for (let sAffId in subscriptionRecords) {
                    if (sAffId === 'length') continue;
                }
            }
            for (let tabIndex in this.workflowTabs) {
                if (tabIndex === 'length') continue;
                let wfTabId = this.workflowTabs[tabIndex].wfTabId;
                let initialRecords = this.serverAffiliations[wfTabId];
                if (!initialRecords) continue;
                let thisTabRecords = {};
                let affIds = Object.keys(initialRecords);
                // eslint-disable-next-line no-loop-func
                affIds.forEach((affId) => {
                    // NOTE: We are using either subscription data or server data here
                    let currAffId = findRenewalAff(affId);
                    thisTabRecords[currAffId] = currAffId !== affId
                        ? this.recordStore.records.aff[currAffId]
                        : subscriptionRecords[currAffId] || initialRecords[currAffId];
                });
                output[wfTabId] = formatActivityAffiliations(thisTabRecords);
            }
            return output;
        },
        visibleTabs() {
            return this.workflowTabs.find((tab) => tab.show);
        },
        loaderVisible() {
            let formattedWorkflowConfigs = library.cloneObj(this.formattedWorkflowConfigs);
            let tab = this.workflowTabs.find((iTab) => iTab.tabIndex === this.selectedTab);
            if (!tab) return false;
            return !formattedWorkflowConfigs[tab.wfTabId]
                || !formattedWorkflowConfigs[tab.wfTabId].length
                || this.childPendingSubscriptionsLength;
        }
    },
    methods: {
        targetItem(item, event) {
            this.$emit('update:targetedItem', item, event);
        },
        updateOrder(orderData) {
            this.$emit('update:itemOrder', orderData);
        },
        updateIncludedItems(wfTabId, itemsLength) {
            this.workflowStepStatus[wfTabId].include = itemsLength;
            if (itemsLength) { // TODO Temp fix for workflow with no included steps, need a better fix for "loaded" criteria
                setTimeout(() => {
                    this.workflowTabsSettings[wfTabId].loading = false;
                    this.workflowTabsError[wfTabId] = 0;
                }, 100);
            } else {
                this.workflowTabsError[wfTabId] = 'No activities included or do not fit for this workflow.';
            }
        },
        updateEnabledItems(wfTabId, itemsLength) {
            this.workflowStepStatus[wfTabId].enable = itemsLength;
        },
        updatePendingSubscriptions(pendingSubscriptions) {
            if (
                (this.childPendingSubscriptionsLength === 0 && pendingSubscriptions.length !== 0)
                || (this.childPendingSubscriptionsLength !== 0 && pendingSubscriptions.length === 0)
            ) {
                this.childPendingSubscriptionsLength = pendingSubscriptions.length;
            }
        },
        changeSelectedTab(tabVal) {
            this.selectedTab = tabVal;
            this.loadWorkflowDetails(this.workflowTabs[tabVal]);
        },
        async onCreate() {
            await library.prepLogicParsingToFront();
            this.logicParserReady = true;
            this.statusOptions = await configStore.attrListOptions("aff_status_2");
            this.reasonOptions = await configStore.attrListOptions("aff_reason_2");
        },
        updateTabVisibility() {
            let contextOrg = this.orgId;
            let selectedTabVisible = false;
            let firstVisibleTab = false;
            this.workflowTabs.forEach((wfTab) => {
                let show = false;
                let wfStatus = wfTab.workflowStatus;
                if (this.controls.SHOW_HIDDEN_WORKFLOWS.value) show = true;
                else if (wfStatus === 'In Progress') show = true;
                else if (wfStatus === 'Waiting') {
                    let hasExpiringAff = false;
                    let waitingRecords = Array.isArray(this.workflowRecords[wfTab.wfTabId]) && this.workflowRecords[wfTab.wfTabId][0];
                    if (waitingRecords && waitingRecords?.affiliations) {
                        for (let iAffId in waitingRecords.affiliations) {
                            if (iAffId === 'length') continue;
                            let iAffiliation = waitingRecords.affiliations[iAffId];
                            if (iAffiliation.AFF_COMPLIANCE_STATUS_ID === 3) {
                                hasExpiringAff = true;
                                break;
                            }
                        }
                    }
                    let enabledSteps = this.workflowStepStatus?.[wfTab.wfTabId]?.enable;
                    show = hasExpiringAff || !!enabledSteps;
                }
                if (contextOrg && wfTab.orgId !== contextOrg) show = false;
                wfTab.show = show;
                if (show) {
                    if (this.selectedTab === wfTab.tabIndex) selectedTabVisible = true;
                    if (firstVisibleTab === false) firstVisibleTab = wfTab.tabIndex;
                }
            });
            if (!selectedTabVisible && firstVisibleTab !== false) {
                this.changeSelectedTab(firstVisibleTab);
            }
            // TODO Remove this temporary fix after we get subscription tracking under control
            if (!this.isWorkflowFullyLoadTimedout) {
                setTimeout(() => {
                    this.isWorkflowFullyLoadTimedout = true;
                }, 15000);
            }
        },
        // eslint-disable-next-line consistent-return
        loadWorkflowTabs() {
            // Generate tabs based off of assignment orgs with workflows
            if (this.editorWorkflowConfig || library.objIsEmpty(this.assignmentList)) return this.workflowTabs;
            let tabIndex = 0;
            // Process each assignment
            // Check for workflows that aren't "Completed"
            // Find any org that has attached workflow and create a tab
            let workflowExists = false;
            Object.values(this.assignmentList).forEach((assignment) => {
                let assignmentOrgs = assignment?.LINKED_RECORDS ? Object.values(library.filterObj(assignment.LINKED_RECORDS, ['VOL', 'OPP'], true)) : [];
                // Get Org Details
                let assignmentOrgDetails = {}; // All assignment org info is added to the tab (down below)
                assignmentOrgs.forEach((aOrgId) => {
                    let wfRecord = this.recordStore.records.org[aOrgId];
                    if (!wfRecord) return;
                    assignmentOrgDetails[aOrgId] = {
                        ORG_TITLE: wfRecord.ORG_TITLE,
                        WORKFLOW_ID: wfRecord.WORKFLOW_ID,
                        WORKFLOW_TITLE: wfRecord.WORKFLOW_TITLE
                    };
                });
                // Get Entry Point Details
                let entryOrg1;
                let entryOrg2;
                if (Array.isArray(assignment?.ENTRY_POINT_ORG_ID) && assignment.ENTRY_POINT_ORG_ID.length) {
                    entryOrg1 = assignment?.ENTRY_POINT_ORG_ID[0] && this.recordStore.records.org[assignment.ENTRY_POINT_ORG_ID[0]].ORG_TITLE;
                    entryOrg2 = assignment?.ENTRY_POINT_ORG_ID[1] && this.recordStore.records.org[assignment.ENTRY_POINT_ORG_ID[1]].ORG_TITLE;
                }
                assignmentOrgs.forEach((aOrgId) => {
                    let wfRecord = assignmentOrgDetails[aOrgId];
                    // TODO call all orgs at same time as assignments
                    let workflowIds = wfRecord.WORKFLOW_ID;
                    if (!workflowIds || (Array.isArray(workflowIds) && library.objIsEmpty(workflowIds))) return;
                    workflowExists = true;
                    if (!Array.isArray(workflowIds)) workflowIds = [workflowIds];
                    workflowIds.forEach((workflowId) => {
                        let show = true;
                        if (!entryOrg2) entryOrg2 = wfRecord.ORG_TITLE;
                        // If there is a restricted workflow type, find values
                        // Only continue if the vol has an assigned workflow that matches the value
                        const restrictTypeObj = this.config?.restrictWorkflowTypes;
                        if (restrictTypeObj) {
                            const restrictTypes = Object.entries(restrictTypeObj);
                            let filterTypes = restrictTypes
                                .filter((workflowOption) => workflowOption[1])
                                .map((workflowOption) => Number(workflowOption[0]));
                            if (filterTypes.length && filterTypes.indexOf(workflowId) === -1) return;
                        }
                        // Else setup workflow tab
                        // Note: Tab visibility is now processed in updateTabVisibility
                        let workflowStatus = assignment?.AFF_ASSIGNMENT_WORKFLOW_STATUS?.[workflowId];
                        if (!workflowStatus) workflowStatus = 'In Progress';
                        // Set show property
                        if ((workflowStatus === 'Completed' || workflowStatus === 'Not Started' || workflowStatus === 'Waiting' || workflowStatus === 'Transferred') && !this.controls.SHOW_HIDDEN_WORKFLOWS.value) show = false;
                        let wfTabId = assignment.ID + '_' + workflowId;
                        // Exit if we have a tab configured already
                        if (this.workflowTabs.find((tab) => tab.wfTabId === wfTabId)) return;
                        // let loading = !this.workflowTabsSettings[wfTabId] || this.workflowTabsSettings[wfTabId].loading;
                        // Get step status from the itemList component
                        if (!this.workflowStepStatus[wfTabId]) this.workflowStepStatus[wfTabId] = {};
                        let title = '<span class="workflow-title">' + wfRecord.WORKFLOW_TITLE + '</span> for ';
                        if (entryOrg1 && entryOrg2) {
                            title = title + '<span class="program-title">' + entryOrg2 + '</span>'
                                + '<br>at <span class="location-title">' + entryOrg1 + '</span>';
                        }
                        else if (entryOrg1) {
                            title = title + '<span class="location-title">' + entryOrg1 + '</span>';
                        }
                        else if (entryOrg2) {
                            title = title + '<span class="program-title">' + entryOrg2 + '</span>';
                        }
                        let newTab = {
                            title,
                            useVueSlot: true,
                            slotTemplateId: 'tabList_item_' + tabIndex,
                            assignmentId: assignment.ID,
                            orgId: aOrgId,
                            assignmentOrgDetails,
                            show,
                            workflowId,
                            wfTabId,
                            workflowStatus,
                            tabIndex,
                            loading: true
                        };
                        // TODO if the tab doesn't already exist
                        this.workflowTabs.push(newTab);
                        if ((newTab.show || workflowStatus === 'Waiting') && (this.selectedTab === tabIndex || this.selectedTab === null)) this.changeSelectedTab(tabIndex);
                        if (this.waitingFirstWorkflowLoad) this.waitingFirstWorkflowLoad = false;
                        tabIndex++;
                    });
                });
            });
            this.updateTabVisibility();
            if (this.workflowExists && !workflowExists) this.workflowExists = false;
            return true;
        },
        loadWorkflowDetails(tabDetails) {
            if (typeof tabDetails !== 'object') return;
            let { wfTabId, assignmentId, workflowId } = tabDetails;
            if (!this.workflowTabRequests[wfTabId] || this.workflowTabRequests[wfTabId].status === 'failure') { // Send the initial request for details
                this.workflowTabRequests[wfTabId] = { status: 'pending' };
                // library.showLoadingIndicator();
                // TODO refactor this to put workflows inside of recordStore or configStore
                let volId = this.contextVolId || "";
                let wfRequestFields = {
                    11: this.shiftFields,
                    32: this.affFields
                };
                this.sDs.post('sds_getWorkflowDetails', volId, assignmentId, workflowId, wfRequestFields, { strictTypeResults: true })
                    .then((response) => {
                        if (response.status === 'success' && response.data) {
                            let resData = response.data;
                            // get Lbes
                            if (!resData?.WORKFLOW?.[workflowId]) {
                                this.workflowTabsError[wfTabId] = 'Can not retrieve data for current workflow.';
                            }
                            for (let reswfId in resData.WORKFLOW) {
                                let workflowResponse = resData.WORKFLOW[reswfId];
                                // Update relatedRecords in data()
                                let relatedRecords = {
                                    activities: workflowResponse.ACTIVITY_ENTITY,
                                    affiliations: {},
                                    lbes: {},
                                    waivers: {},
                                    trainings: {},
                                    // shifts: {},  // accessed through the recordStore
                                    ASSIGNMENT_DETAIL_RECORD: {},
                                    ASSIGNMENT: {},
                                    assignment: {},
                                    VOL_AE_AFFILIATION_ID: workflowResponse.VOL_AE_AFFILIATION_ID
                                };
                                if (!this.serverAffiliations) this.serverAffiliations = {};
                                if (resData?.AFFILIATION && this.serverAffiliations !== null) this.serverAffiliations[wfTabId] = resData.AFFILIATION;
                                if (resData.LOGBOOK_ENTRIES) relatedRecords.lbes = resData.LOGBOOK_ENTRIES;
                                // TODO refactor these to exclude relatedRecords and just use the resData with keys like WAIVERS
                                // ie: Don't convert them to lowercase, it's not necessary
                                if (!library.objIsEmpty(resData.WAIVERS)) relatedRecords.waivers = resData.WAIVERS;
                                if (!library.objIsEmpty(resData.TRAININGS)) relatedRecords.trainings = resData.TRAININGS;
                                if (!library.objIsEmpty(resData.SCHEDULE_SHIFT_PLACEMENTS)) {
                                    // Merge vol's shift placements with recordStore
                                    library.mergeObj(this.recordStore.records.vol[volId].shifts, resData.SCHEDULE_SHIFT_PLACEMENTS);
                                    this.recordStore.setModified({ vol: [volId] });
                                }
                                if (resData.ASSIGNMENT) {
                                    relatedRecords.ASSIGNMENT = resData.ASSIGNMENT[resData.ASSIGNMENT_ID];
                                    // This is the assignment with basic records instead of IDs
                                    relatedRecords.assignment = locals.formatAssignmentIds(
                                        resData.ASSIGNMENT[resData.ASSIGNMENT_ID].LINKED_RECORDS,
                                        resData.ASSIGNMENT[resData.ASSIGNMENT_ID].LINKED_AFFILIATIONS,
                                        resData.ASSIGNMENT[resData.ASSIGNMENT_ID].ENTRY_POINT_ORG_ID
                                    );
                                }
                                let endpointRecord = resData.ASSIGNMENT_DETAIL_RECORD_ID
                                    && resData.ASSIGNMENT_DETAIL_RECORD[resData.ASSIGNMENT_DETAIL_RECORD_ID];
                                if (!library.objIsEmpty(endpointRecord)) {
                                    relatedRecords.ASSIGNMENT_DETAIL_RECORD = endpointRecord;
                                    relatedRecords.DETAIL_RECORD_ID = endpointRecord.DETAIL_RECORD_ID[0];
                                }
                                if (!library.isEqualObj(this.relatedRecords[wfTabId], relatedRecords)) {
                                    this.relatedRecords[wfTabId] = relatedRecords;
                                }
                                // Generate the tabs after related records are set up
                                let newTabSettings = resData.ACTIVITY_ENTITY && locals.formatWorkflowActivityEntities(workflowResponse, resData.ACTIVITY_ENTITY);
                                newTabSettings.loading = true;
                                this.workflowTabsSettings[wfTabId] = newTabSettings;
                            }
                        }
                        // if response is failure leave the workflowTabsSettings[wfTabId] as an empty object
                        library.hideLoadingIndicator();
                        this.workflowTabRequests[wfTabId].status = response.status;
                    });
            }
        }
    },
    components: {
        'item-list': defineAsyncComponent(() => import('@common/itemList.vue')),
        tabList: defineAsyncComponent(() => import('@common/tabList/tabList.vue'))
    }
};

componentStore.initComponent(workflowViewerCmp, workflowViewerConfig);

export default workflowViewerCmp;
</script>
<style>
/* Loader TODO: Move this to the loader component */
.loader-animation-container {
    display: inline-block;
    width: 60px;
}
.loader-animation {
    display: grid;
    grid-template-columns: repeat(5, 10px);
    gap: 3px;
    justify-content: center;
    align-items: center;
}
.ball {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    animation: wave 1.5s ease-in-out infinite;
    background-color:#EC135E;
}
.ball:nth-child(2) {
    animation-delay: -0.2s;
    background-color:#EF7D22;
}
.ball:nth-child(3) {
    animation-delay: -0.4s;
    background-color:#F7CE46;
}
.ball:nth-child(4) {
    animation-delay: -0.6s;
    background-color:#8BA93D;
}
.ball:nth-child(5) {
    animation-delay: -0.8s;
    background-color:#00ADC5;
}
@keyframes wave {
    0%, 100% {
        transform: translateY(6px);
    }
    50% {
        transform: translateY(-6px);
    }
}
/* End Loader */
</style>
<style scoped>
:deep(.expiration-status) {
    font-size: 90%;
    margin-top: 15px;
}
:deep(.renewal-in-progress-msg) {
    font-size: 1rem;
    color: #494949
}
:deep(.expiration-flag) {
    margin-right: 10px;
    font-size: 16px;
}
:deep(.current) { color: #16a34a }
:deep(.expiring) { color: #FB8432 }
:deep(.expired) { color: #dc2626 }
:deep(.activity-expiration) {
    display: flex;
    gap: 1rem;
    margin: 1rem 0;
    padding: 1rem;
    border-radius: 0.5rem;
    background: #fef4eb;
    width: fit-content
}
@media (max-width: 620px) {
    :deep(.activity-expiration) {
        flex-direction: column;
    }
    :deep(.btn-renewal.coordinator-action-button) {
        margin: 0 5px;
    }
}
:deep(.activity-expiration) {
    display: flex;
    gap: 1rem;
    margin: 1rem 0;
    padding: 1rem;
    border-radius: 0.5rem;
    background: #fef4eb;
    width: fit-content
}
:deep(.activity-expiration > .expiration-text) {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    color: #060606;
}
:deep(.activity-expiration > i) {
    font-size: 2rem;
    color: #ff3e33;
    animation: vibrate 30s infinite ease-in-out;
}
:deep(.activity-expiration > button) {
    align-self: start;
    padding: 0.75rem 1.25rem;
    font-size: 1.25rem;
    background: rgb(22, 92, 148);
    border-radius: 0.5rem;
    color: #fff;
    text-decoration: none
}
:deep(.activity-expiration > button:hover) {
    background: #2364f1;
}
:deep(.aff-info-text) {
    display: inline-block;
    padding-right: 8px;
}
:deep(.coordinator-actions), :deep(.coordinator-info) {
    display: inline-block;
    margin-top: 15px;
}
:deep(.coordinator-info) {
    border-right: 1px solid #bbbbbb;
}
:deep(.coordinator-action-button) {
    border: 0 none;
    transition: background .1s ease-in;
    background: rgba(255, 255, 255, .20);
    border-radius: 15px;
    color: #2b2b86;
    padding: 5px 15px;
    margin: 5px;
    font-size: 14px;
    display: inline-block;
    text-decoration: underline;
}
:deep(.coordinator-action-button:hover) {
    background: rgba(0, 0, 0, .08);
    transition: all .1s ease-in-out;
}
:deep(.history-button .e-icons) {
    margin-right: 3px;
    position: relative;
    top: 2px;
    font-size: 13px;
}
.workflow-header {
    display: flex;
    padding: 5px;
    border-bottom: 1px solid #b0b0b0;
    color: #383c50;
}
.workflow-loader p {
    display: inline-block;
    line-height: 24px;
    font-size: 16px;
    margin: 0 0 0 15px;
    vertical-align: top;
}
.no-workflows-message {
    font-size: 16px;
}
.workflow-status {
    text-align: right;
    flex-grow: 9;
    font-size: 16px;
    font-weight: bold;
}
.assignment-id {
    font-size: 14px;
}
:deep(.step-not-visible) {
    display: none;
}
@keyframes vibrate {
    0%, 96.9% { transform: rotate(0deg) translateX(0); }
    97.0% { transform: rotate(-3deg) translateX(-2px); }
    97.1% { transform: rotate(3deg)  translateX(2px); }
    97.2% { transform: rotate(-3deg) translateX(-2px); }
    97.3% { transform: rotate(3deg)  translateX(2px); }
    97.4% { transform: rotate(-2.5deg) translateX(-1.8px); }
    97.5% { transform: rotate(2.5deg)  translateX(1.8px); }
    97.6% { transform: rotate(-2.5deg) translateX(-1.8px); }
    97.7% { transform: rotate(2.5deg)  translateX(1.8px); }
    97.8% { transform: rotate(-2deg) translateX(-1.5px); }
    97.9% { transform: rotate(2deg)  translateX(1.5px); }
    98.0% { transform: rotate(-2deg) translateX(-1.5px); }
    98.1% { transform: rotate(2deg)  translateX(1.5px); }
    98.2% { transform: rotate(-1.5deg) translateX(-1px); }
    98.3% { transform: rotate(1.5deg)  translateX(1px); }
    98.4% { transform: rotate(-1.5deg) translateX(-1px); }
    98.5% { transform: rotate(1.5deg)  translateX(1px); }
    98.6% { transform: rotate(-1deg) translateX(-0.8px); }
    98.7% { transform: rotate(1deg)  translateX(0.8px); }
    98.8% { transform: rotate(-0.5deg) translateX(-0.5px); }
    98.9% { transform: rotate(0.5deg)  translateX(0.5px); }
    99%, 100% { transform: rotate(0deg) translateX(0); }
}

</style>
